import React, { useState, useEffect, useCallback } from 'react';
import { providers, categories, settings } from '../services/api';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import ModernProviderCard from './ModernProviderCard';
import Navigation from './Navigation';
import { Search, Filter, MapPin, Tag, User, LogOut, Menu, X, Star, TrendingUp, Clock, CheckCircle, Calendar, MessageSquare, Sparkles, ArrowRight, Camera, Music, Utensils, Flower2, Home, Car, Cake, Headphones, Video, Palette, Scissors, Shirt, FileText, Mic2, Gift, Wine, Heart } from 'lucide-react';
import { weddingColors } from '../styles/weddingTheme';

function ModernProviderList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [providerList, setProviderList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroImage, setHeroImage] = useState('');
  const [heroTitle, setHeroTitle] = useState('Wedding Service Providers');
  const [heroSubtitle, setHeroSubtitle] = useState('Find trusted professionals for your perfect day');
  const [heroOverlayColor, setHeroOverlayColor] = useState('#000000');
  const [heroOverlayOpacity, setHeroOverlayOpacity] = useState(0.5);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Initialize filters from URL params
  const categoryParam = searchParams.get('category') || '';
  const [filters, setFilters] = useState({
    category: categoryParam,
    province: '',
    priceRanges: [],  // Array of selected price ranges: ['$', '$$', etc]
    sortBy: 'rating'  // 'rating', 'reviews', or 'price'
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [initialLoad, setInitialLoad] = useState(true);
  const [featuredProviders, setFeaturedProviders] = useState([]);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteCategories, setQuoteCategories] = useState([]);
  const [showSearchModal, setShowSearchModal] = useState(false);

  // Load providers with proper memoization
  const loadProviders = useCallback(async () => {
    if (!initialLoad) {
      setLoading(true);
    }

    try {
      const response = await providers.getAll(filters);
      setProviderList(response.data?.providers || response.providers || []);
    } catch (error) {
      console.error('Error loading providers:', error);
      setProviderList([]);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [filters, initialLoad]);

  // Load categories once on mount
  const loadCategories = useCallback(async () => {
    try {
      const response = await categories.getWithCounts();
      const data = response.data || response;
      const cats = data.categories || data || [];
      setCategoryList(Array.isArray(cats) ? cats : []);
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategoryList([]);
    }
  }, []);

  // Load hero settings once on mount
  const loadHeroSettings = useCallback(async () => {
    try {
      const response = await settings.getAll();
      const siteSettings = response.data || {};

      if (siteSettings.hero_image_url) {
        const imageUrl = siteSettings.hero_image_url;
        setHeroImage(imageUrl.startsWith('http') ? imageUrl : `https://api.bridalconnect.co.za${imageUrl}`);
      }
      if (siteSettings.hero_title) {
        setHeroTitle(siteSettings.hero_title);
      }
      if (siteSettings.hero_subtitle) {
        setHeroSubtitle(siteSettings.hero_subtitle);
      }
      if (siteSettings.hero_overlay_color) {
        setHeroOverlayColor(siteSettings.hero_overlay_color);
      }
      if (siteSettings.hero_overlay_opacity !== undefined) {
        setHeroOverlayOpacity(parseFloat(siteSettings.hero_overlay_opacity));
      }
    } catch (error) {
      console.error('Error loading hero settings:', error);
    }
  }, []);

  // Update filters when URL params change
  useEffect(() => {
    const categoryParam = searchParams.get('category') || '';
    setFilters(prev => ({
      ...prev,
      category: categoryParam
    }));
  }, [searchParams]);

  // Load featured providers
  const loadFeaturedProviders = useCallback(async () => {
    try {
      const response = await providers.getAll({});
      const allProviders = response.data?.providers || response.providers || [];
      // Get top 6 featured providers
      setFeaturedProviders(allProviders.slice(0, 6));
    } catch (error) {
      console.error('Error loading featured providers:', error);
    }
  }, []);

  // Initial load - run everything in parallel
  useEffect(() => {
    Promise.all([
      loadProviders(),
      loadCategories(),
      loadHeroSettings(),
      loadFeaturedProviders()
    ]);
  }, []); // Only on mount

  // Load providers when filters change (but not on initial mount)
  useEffect(() => {
    if (!initialLoad) {
      loadProviders();
    }
  }, [filters, loadProviders]);

  // Filter and sort providers
  const filteredProviders = providerList
    .filter(provider => {
      // Search query filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = (
          provider.business_name?.toLowerCase().includes(query) ||
          provider.contact_person?.toLowerCase().includes(query) ||
          provider.category_name?.toLowerCase().includes(query) ||
          provider.city?.toLowerCase().includes(query)
        );
        if (!matchesSearch) return false;
      }

      // Price range filter
      if (filters.priceRanges.length > 0 && provider.price_range) {
        const providerRanges = Array.isArray(provider.price_range)
          ? provider.price_range
          : [];
        // Show if ANY provider range matches ANY selected range
        const hasMatchingRange = providerRanges.some(range =>
          filters.priceRanges.includes(range)
        );
        if (!hasMatchingRange) return false;
      }

      return true;
    })
    .sort((a, b) => {
      // Sort by selected criteria
      switch (filters.sortBy) {
        case 'reviews':
          const aReviews = (a.google_review_count || 0) + (a.facebook_review_count || 0);
          const bReviews = (b.google_review_count || 0) + (b.facebook_review_count || 0);
          return bReviews - aReviews;

        case 'price':
          // Sort by price (Budget first)
          const priceOrder = { '$': 1, '$$': 2, '$$$': 3, '$$$$': 4 };
          const aPrice = a.price_range?.[0] ? priceOrder[a.price_range[0]] || 999 : 999;
          const bPrice = b.price_range?.[0] ? priceOrder[b.price_range[0]] || 999 : 999;
          return aPrice - bPrice;

        case 'rating':
        default:
          const aRating = parseFloat(a.google_rating) || 0;
          const bRating = parseFloat(b.google_rating) || 0;
          return bRating - aRating;
      }
    });

  // Handle price range selection (must be consecutive)
  const handlePriceRangeToggle = (range) => {
    const priceOrder = ['$', '$$', '$$$', '$$$$'];
    const currentRanges = [...filters.priceRanges];
    const index = priceOrder.indexOf(range);

    if (currentRanges.includes(range)) {
      // Remove the range
      setFilters({
        ...filters,
        priceRanges: currentRanges.filter(r => r !== range)
      });
    } else {
      // Add the range
      const newRanges = [...currentRanges, range];
      // Sort by price order
      newRanges.sort((a, b) => priceOrder.indexOf(a) - priceOrder.indexOf(b));

      // Check if consecutive
      const indices = newRanges.map(r => priceOrder.indexOf(r));
      const isConsecutive = indices.every((val, i, arr) =>
        i === 0 || val === arr[i - 1] + 1
      );

      if (isConsecutive) {
        setFilters({ ...filters, priceRanges: newRanges });
      }
    }
  };

  // Check if a price range is disabled (not consecutive)
  const isPriceRangeDisabled = (range) => {
    if (filters.priceRanges.length === 0) return false;

    const priceOrder = ['$', '$$', '$$$', '$$$$'];
    const currentRanges = filters.priceRanges;
    const rangeIndex = priceOrder.indexOf(range);

    if (currentRanges.includes(range)) return false; // Already selected

    const minSelected = Math.min(...currentRanges.map(r => priceOrder.indexOf(r)));
    const maxSelected = Math.max(...currentRanges.map(r => priceOrder.indexOf(r)));

    // Can only select adjacent to current range
    return rangeIndex !== minSelected - 1 && rangeIndex !== maxSelected + 1;
  };

  // Category icons mapping (matched to actual database slugs)
  const categoryIcons = {
    'dj': Headphones,
    'photographer': Camera,
    'music-entertainment': Headphones,
    'photography': Camera,
    'videography': Video,
    'attire-styling': Shirt,
    'beauty-styling': Palette,
    'ceremony-services': Heart,
    'decor-flowers': Flower2,
    'entertainment-planning': Mic2,
    'entertainment-specialty': Sparkles,
    'event-party-services': Gift,
    'food-beverage': Utensils,
    'logistics': Car,
    'logistics-comfort': Home,
    'logistics-safety': Car,
    'media-content': Video,
    'paper-goods-signage': FileText,
    'planning': CheckCircle,
    'production-av': Music,
    'support': User,
    'venue': Home
  };

  // Wedding planning timeline
  const planningTimeline = [
    { months: '12-9', title: 'Getting Started', tasks: ['Book venue', 'Hire photographer', 'Book entertainment', 'Choose caterer'], icon: Home },
    { months: '9-6', title: 'Key Vendors', tasks: ['Book florist', 'Book videographer', 'Order cake'], icon: Headphones },
    { months: '6-3', title: 'Details', tasks: ['Finalise decor', 'Book transport', 'Confirm all vendors'], icon: Car },
    { months: '3-0', title: 'Final Touches', tasks: ['Final venue walkthrough', 'Vendor confirmations', 'Final payments'], icon: CheckCircle }
  ];

  // Seasonal content (can be dynamic based on current month)
  const seasonalContent = {
    title: '2025 Wedding Trends',
    items: [
      { name: 'Outdoor Garden Weddings', category: 'venues' },
      { name: 'Live Band Entertainment', category: 'music-dj' },
      { name: 'Farm-to-Table Catering', category: 'catering' },
      { name: 'Minimalist Floral Designs', category: 'florists' }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      <Navigation />

      {/* Hero Section */}
      <div className="relative h-[28rem] overflow-hidden bg-gradient-to-r from-purple-600 to-pink-600">
        {heroImage && (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${heroImage})`
            }}
          />
        )}
        <div
          className="absolute inset-0"
          style={{
            backgroundColor: `${heroOverlayColor}${Math.round(heroOverlayOpacity * 255).toString(16).padStart(2, '0')}`
          }}
        />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-white px-4">
          <h1 className="text-5xl md:text-6xl font-bold text-center mb-4 drop-shadow-lg">
            {heroTitle}
          </h1>
          <p className="text-xl md:text-2xl text-center mb-2 drop-shadow">
            KwaZulu-Natal's curated directory of trusted wedding professionals
          </p>

          {/* Search Button - Opens Modal */}
          <div className="w-full max-w-2xl">
            <button
              onClick={() => setShowSearchModal(true)}
              className="w-full flex items-center gap-3 pl-12 pr-4 py-4 rounded-full text-gray-500 bg-white shadow-xl hover:shadow-2xl transition-all text-lg text-left relative"
            >
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              Search by business name, category, or location...
            </button>
          </div>
        </div>

      </div>


      {/* Search Results Modal */}
      {showSearchModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 pt-20 overflow-y-auto" onClick={() => {
          setShowSearchModal(false);
          setSearchQuery('');
        }}>
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <h3 className="text-2xl font-bold text-gray-800">Search Providers</h3>
                <button onClick={() => {
                  setShowSearchModal(false);
                  setSearchQuery('');
                }} className="ml-auto text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
              {/* Search Bar Inside Modal */}
              <div className="relative mb-4">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by business name, category, or location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                  className="w-full pl-12 pr-4 py-3 rounded-lg text-gray-900 bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-lg"
                />
              </div>

              {/* Category Filter */}
              <div className="flex items-center gap-3 flex-wrap pb-3 border-b border-gray-200">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Filter className="w-4 h-4" />
                  <span className="font-medium">Filter:</span>
                </div>

                <select
                  value={filters.category}
                  onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                  className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 appearance-none cursor-pointer"
                >
                  <option value="">All Categories</option>
                  {categoryList.map(cat => (
                    <option key={cat.id || cat.slug} value={cat.slug}>{cat.name}</option>
                  ))}
                </select>

                {filters.category && (
                  <button
                    onClick={() => setFilters({ ...filters, category: '' })}
                    className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                  >
                    <X className="w-4 h-4" />
                    Clear
                  </button>
                )}
              </div>

              {searchQuery && (
                <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                  <span className="font-medium">{filteredProviders.length} {filteredProviders.length === 1 ? 'result' : 'results'} found</span>
                </div>
              )}
            </div>
            <div className="overflow-y-auto flex-1 p-6">
              {filteredProviders.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredProviders.map((provider) => (
                    <div
                      key={provider.id}
                      onClick={() => {
                        navigate(`/provider/${provider.id}`);
                        setShowSearchModal(false);
                      }}
                      className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all cursor-pointer group"
                    >
                      <div className="flex gap-4">
                        {provider.banner_url ? (
                          <img
                            src={provider.banner_url.startsWith('http') ? provider.banner_url : `https://api.bridalconnect.co.za${provider.banner_url}`}
                            alt={provider.business_name}
                            className="w-20 h-20 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg flex items-center justify-center">
                            <span className="text-2xl font-bold text-purple-600">
                              {provider.business_name?.charAt(0) || '?'}
                            </span>
                          </div>
                        )}
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-800 group-hover:text-purple-600 transition-colors">
                            {provider.business_name}
                          </h4>
                          <p className="text-sm text-gray-600 mt-1">{provider.category_name}</p>
                          {provider.city && (
                            <div className="flex items-center gap-1 mt-2 text-sm text-gray-500">
                              <MapPin className="w-4 h-4" />
                              {provider.city}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-xl text-gray-600 mb-2">No results found</p>
                  <p className="text-gray-500">Try searching for a different business name, category, or location</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sticky Category Filter Bar */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-500">Category:</span>
            <div className="relative">
              <select
                value={filters.category}
                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                className="appearance-none pl-4 pr-10 py-2.5 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl text-sm font-semibold text-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-400 cursor-pointer hover:border-purple-300 transition-all min-w-[220px]"
              >
                <option value="">All Categories ({providerList.length})</option>
                {categoryList
                  .filter(category => parseInt(category.provider_count || 0) > 0)
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((category) => {
                    const count = parseInt(category.provider_count || 0);
                    return (
                      <option key={category.id} value={category.slug}>
                        {category.name} ({count})
                      </option>
                    );
                  })}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            {filters.category && (
              <button
                onClick={() => setFilters(prev => ({ ...prev, category: '' }))}
                className="text-sm text-purple-600 hover:text-purple-800 font-medium flex items-center gap-1 px-3 py-2 rounded-lg hover:bg-purple-50 transition-colors"
              >
                <X className="w-4 h-4" />
                Clear
              </button>
            )}
            <span className="ml-auto text-sm text-gray-400">
              {filters.category
                ? `${providerList.filter(p => {
                    const cats = p.categories || p.all_categories || [];
                    return cats.some(c => c.slug === filters.category) || p.category_slug === filters.category;
                  }).length} providers`
                : `${providerList.length} providers`
              }
            </span>
          </div>
        </div>
      </div>

      {(() => {
        return (
          <>
            {/* Provider Results - always show */}
            {(
              <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Loading State */}
                {loading && initialLoad && (
                  <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                  </div>
                )}

                {/* Provider Cards Grid */}
                {!loading && filteredProviders.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {filteredProviders.map((provider) => (
                      <ModernProviderCard key={provider.id} provider={provider} />
                    ))}
                  </div>
                )}

                {/* No Results */}
                {!loading && !initialLoad && filteredProviders.length === 0 && (
                  <div className="text-center py-16">
                    <div className="text-gray-400 mb-4">
                      <Search className="w-16 h-16 mx-auto" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">No providers found</h3>
                    <p className="text-gray-500">Try adjusting your search or filters</p>
                  </div>
                )}
              </div>
            )}

          </>
        );
      })()}

      {/* Wedding Page CTA */}
      <div className="max-w-7xl mx-auto px-4 mt-16">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-rose-500 via-pink-500 to-purple-500 p-8 md:p-12">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
          <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                Create Your Free Wedding Page
              </h2>
              <p className="text-lg text-rose-100 mb-2">
                Get your own wedding website at bridalconnect.co.za/yournames
              </p>
              <ul className="text-rose-100 space-y-1 mb-6">
                <li className="flex items-center gap-2 justify-center md:justify-start">
                  <CheckCircle className="w-4 h-4 text-white flex-shrink-0" /> Online RSVP with WhatsApp sharing
                </li>
                <li className="flex items-center gap-2 justify-center md:justify-start">
                  <CheckCircle className="w-4 h-4 text-white flex-shrink-0" /> Beautiful templates & photo gallery
                </li>
                <li className="flex items-center gap-2 justify-center md:justify-start">
                  <CheckCircle className="w-4 h-4 text-white flex-shrink-0" /> Guest list management & meal tracking
                </li>
                <li className="flex items-center gap-2 justify-center md:justify-start">
                  <CheckCircle className="w-4 h-4 text-white flex-shrink-0" /> 100% free — no hidden costs
                </li>
              </ul>
            </div>
            <div className="flex flex-col items-center gap-3">
              <button
                onClick={() => navigate('/couple/register')}
                className="px-8 py-4 bg-white text-rose-600 rounded-xl font-bold text-lg hover:bg-rose-50 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Get Started — It's Free
              </button>
              <button
                onClick={() => navigate('/couple/login')}
                className="text-white/80 hover:text-white text-sm underline underline-offset-2"
              >
                Already have an account? Sign in
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-purple-900 to-pink-900 text-white py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold mb-4">KwaZulu-Natal Wedding Directory</h3>
            <p className="text-purple-200 mb-6">
              Connecting couples with trusted KZN wedding professionals
            </p>
          </div>

          <div className="border-t border-purple-700 pt-6">
            <p className="text-sm text-purple-300 text-center">
              © 2026 BridalConnect. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default ModernProviderList;